import { useEffect, useMemo } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
  ADD_VETCOIN_TRANSACTION,
  ADD_VETCOIN_TRANSACTION_FOR_POST,
  BUY_STORE_ITEM,
  EQUIP_STORE_ITEM,
  UPSERT_USER_VETCOIN_BALANCE,
} from "graphql/vetcoin/vetcoin.mutation";
import {
  GET_STORE_ITEMS,
  GET_USER_VETCOIN_PROFILE,
} from "graphql/vetcoin/vetcoin.query";
import {
  StoreItem,
  StoreItemType,
  UserEquippedItem,
  UserStoreItem,
  VetcoinActionType,
} from "types/vetcoin";
import {
  hasEnoughVetcoins,
  nextBalanceAfterCredit,
  nextBalanceAfterPurchase,
} from "utils/vetcoin";
import {
  VETCOIN_ANIMATION_COMPLETE_EVENT,
  triggerVetcoinAnimation,
} from "utils/vetcoinAnimation";
import { useAuthUser } from "hooks/useAuthUser";

export const VETCOIN_REWARDS = {
  FEEDBACK_REQUEST_SUBMITTED: 12,
  FEEDBACK_EVALUATION_COMPLETED: 18,
  FEEDBACK_COMMENT_CREATED: 5,
  POST_CREATED: 10,
  POST_ANSWER_CREATED: 5,
} as const;

type AwardVetcoinInput = {
  userId: string;
  amount: number;
  actionType: VetcoinActionType;
  sourceFeedbackId?: string | null;
  sourcePostId?: string | null;
  metadata?: Record<string, unknown>;
};

type BuyStoreItemInput = {
  userId: string;
  item: StoreItem;
  skipVetcoinDebit?: boolean;
};

type EquipStoreItemInput = {
  userId: string;
  item: StoreItem;
};

export const useVetcoin = (userId?: string) => {
  const { user } = useAuthUser();
  const client = useApolloClient();
  const { data: storeData, loading: storeLoading } = useQuery(GET_STORE_ITEMS);
  const { data: profileData, loading: profileLoading, refetch } = useQuery(
    GET_USER_VETCOIN_PROFILE,
    {
      variables: { userId: userId || "" },
      skip: !userId,
      fetchPolicy: "cache-and-network",
    },
  );

  const [addVetcoinTransaction] = useMutation(ADD_VETCOIN_TRANSACTION);
  const [addVetcoinTransactionForPost] = useMutation(
    ADD_VETCOIN_TRANSACTION_FOR_POST,
  );
  const [upsertUserVetcoinBalance] = useMutation(UPSERT_USER_VETCOIN_BALANCE);
  const [buyStoreItemMutation] = useMutation(BUY_STORE_ITEM);
  const [equipStoreItemMutation] = useMutation(EQUIP_STORE_ITEM);

  const storeItems = useMemo(
    () => ((storeData?.store_item as StoreItem[]) || []).filter(Boolean),
    [storeData],
  );
  const ownedItems = useMemo(
    () => ((profileData?.user_store_item as UserStoreItem[]) || []).filter(Boolean),
    [profileData],
  );
  const equippedItems = useMemo(
    () =>
      ((profileData?.user_equipped_item as UserEquippedItem[]) || []).filter(Boolean),
    [profileData],
  );
  const balance = useMemo(
    () => profileData?.user_vetcoin_balance?.[0]?.balance ?? 0,
    [profileData],
  );

  const refreshVetcoin = async () => {
    if (!userId) return;
    await refetch({ userId });
  };

  useEffect(() => {
    if (!userId || userId !== user?.user_id) return;

    const handler = () => {
      void refetch({ userId });
    };
    window.addEventListener(VETCOIN_ANIMATION_COMPLETE_EVENT, handler);
    return () => {
      window.removeEventListener(VETCOIN_ANIMATION_COMPLETE_EVENT, handler);
    };
  }, [userId, user?.user_id, refetch]);

  const getCurrentBalance = async (targetUserId: string) => {
    if (!targetUserId) return 0;
    const latest = await client.query({
      query: GET_USER_VETCOIN_PROFILE,
      variables: { userId: targetUserId },
      fetchPolicy: "no-cache",
    });
    return latest.data?.user_vetcoin_balance?.[0]?.balance ?? 0;
  };

  const awardVetcoins = async ({
    userId: targetUserId,
    amount,
    actionType,
    sourceFeedbackId,
    sourcePostId,
    metadata = {},
  }: AwardVetcoinInput) => {
    if (!targetUserId || amount <= 0) return { awarded: false };

    let insertedRows = 0;
    if (sourcePostId) {
      const transactionResponse = await addVetcoinTransactionForPost({
        variables: {
          user_id: targetUserId,
          action_type: actionType,
          direction: "CREDIT",
          amount,
          source_post_id: sourcePostId,
          metadata,
        },
      });
      insertedRows =
        transactionResponse.data?.insert_vetcoin_transaction?.affected_rows ||
        0;
    } else {
      const transactionResponse = await addVetcoinTransaction({
        variables: {
          user_id: targetUserId,
          action_type: actionType,
          direction: "CREDIT",
          amount,
          source_feedback_id: sourceFeedbackId,
          metadata,
        },
      });
      insertedRows =
        transactionResponse.data?.insert_vetcoin_transaction?.affected_rows ||
        0;
    }

    if (!insertedRows) {
      return { awarded: false };
    }

    const currentBalance = await getCurrentBalance(targetUserId);
    const nextBalance = nextBalanceAfterCredit(currentBalance, amount);
    await upsertUserVetcoinBalance({
      variables: {
        user_id: targetUserId,
        balance: nextBalance,
      },
    });

    const shouldAnimate = targetUserId === user?.user_id;
    if (shouldAnimate) {
      // Keep the visible balance frozen until coins reach the header.
      triggerVetcoinAnimation({ amount });
    } else {
      await refreshVetcoin();
    }

    return { awarded: true };
  };

  const buyStoreItem = async ({
    userId: targetUserId,
    item,
    skipVetcoinDebit = false,
  }: BuyStoreItemInput) => {
    const alreadyOwned = ownedItems.some(
      (ownedItem) => ownedItem.store_item_id === item.store_item_id,
    );
    if (alreadyOwned) {
      return { purchased: false, reason: "ALREADY_OWNED" as const };
    }
    const currentBalance = await getCurrentBalance(targetUserId);
    if (!skipVetcoinDebit && !hasEnoughVetcoins(currentBalance, item.cost)) {
      return { purchased: false, reason: "INSUFFICIENT_BALANCE" as const };
    }

    await buyStoreItemMutation({
      variables: {
        user_id: targetUserId,
        store_item_id: item.store_item_id,
      },
    });
    if (!skipVetcoinDebit) {
      await addVetcoinTransaction({
        variables: {
          user_id: targetUserId,
          action_type: "STORE_PURCHASE" as VetcoinActionType,
          direction: "DEBIT",
          amount: item.cost,
          store_item_id: item.store_item_id,
          metadata: { itemSlug: item.slug, itemType: item.type },
        },
      });
      await upsertUserVetcoinBalance({
        variables: {
          user_id: targetUserId,
          balance: nextBalanceAfterPurchase(currentBalance, item.cost),
        },
      });
    }
    await refreshVetcoin();

    return { purchased: true };
  };

  const equipStoreItem = async ({ userId: targetUserId, item }: EquipStoreItemInput) => {
    await equipStoreItemMutation({
      variables: {
        user_id: targetUserId,
        store_item_id: item.store_item_id,
        item_type: item.type,
      },
    });
    await refreshVetcoin();
    return { equipped: true };
  };

  const isOwned = (itemId: string) =>
    ownedItems.some((item) => item.store_item_id === itemId);

  const getEquippedByType = (itemType: StoreItemType) =>
    equippedItems.find((item) => item.item_type === itemType) || null;

  return {
    loading: storeLoading || profileLoading,
    balance,
    storeItems,
    ownedItems,
    equippedItems,
    awardVetcoins,
    buyStoreItem,
    equipStoreItem,
    isOwned,
    getEquippedByType,
    refreshVetcoin,
  };
};
